/* global BigInt */
import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import axios from 'axios';
import { X } from 'lucide-react';
import { apiErrorMessage } from '../utils/apiError';
import { formatMoney } from '../utils/money';
import { apiBase, authHeaders, getToken, resolveOrgId } from '../utils/authHeaders';

export const mktApi = axios.create({ baseURL: `${apiBase()}/api/marketplace` });

export function readMktScope() {
  return {
    orgId: resolveOrgId(),
    token: getToken(),
    locationId: localStorage.getItem('current_location_id') || '',
    department: localStorage.getItem('active_department') || '',
  };
}
export const scopeKey = scope => JSON.stringify(scope);
export const requestConfig = (scope, signal) => ({ headers: authHeaders(scope), signal });

export function useMktScope(refreshToken) {
  const [scope, setScope] = useState(readMktScope);
  useEffect(() => {
    const update = () => {
      const next = readMktScope();
      setScope(previous => (scopeKey(previous) === scopeKey(next) ? previous : next));
    };
    update();
    const events = ['organizationChanged', 'locationChanged', 'fi-department-changed', 'storage'];
    events.forEach(e => window.addEventListener(e, update));
    return () => events.forEach(e => window.removeEventListener(e, update));
  }, [refreshToken]);
  return scope;
}

// The backend answers /capabilities with 200 even when the module is off, so the
// body (not the status code) decides. Anything but an explicit `true` is closed.
export const capabilitiesAllow = caps => Boolean(caps) && caps.enabled === true && caps.can_read === true;

export function useMarketplaceNavEnabled(token, probe = true) {
  const scope = useMktScope(token);
  const key = scopeKey(scope);
  const [allowedKey, setAllowedKey] = useState('');
  useEffect(() => {
    const controller = new AbortController();
    setAllowedKey('');
    if (probe && scope.orgId && scope.token) {
      mktApi.get('/capabilities', requestConfig(scope, controller.signal))
        .then(res => { if (!controller.signal.aborted && capabilitiesAllow(res?.data)) setAllowedKey(key); })
        .catch(() => {});
    }
    return () => controller.abort();
  }, [key, scope, probe]);
  return allowedKey === key;
}

/** True when a request was aborted on purpose (unmount, scope change, StrictMode remount). */
export const isCancelled = e => e?.code === 'ERR_CANCELED' || e?.name === 'CanceledError' || e?.name === 'AbortError';

// A cancelled request is not a failure: return '' so `{error && ...}` renders nothing
// (axios reports an abort with the bare message "canceled").
export const errorMessage = e => (isCancelled(e)
  ? ''
  : apiErrorMessage(e?.response?.data?.detail, e?.message || 'Request failed.'));
export const errorCode = e => {
  const detail = e?.response?.data?.detail;
  return detail && typeof detail === 'object' ? detail.code : undefined;
};

/**
 * Shared request/mutation runtime for a workspace that is remounted (keyed) per scope.
 * `active()` is false after unmount or once org/site/department in storage no longer
 * matches the scope this workspace was rendered for, so late responses are dropped.
 */
export function useMktRuntime(scope) {
  const lifetime = useRef(new AbortController());
  const lock = useRef(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  useEffect(() => {
    const controller = new AbortController();
    lifetime.current = controller;
    return () => controller.abort();
  }, []);
  const active = useCallback(
    () => !lifetime.current.signal.aborted && scopeKey(readMktScope()) === scopeKey(scope),
    [scope],
  );
  const request = useCallback(async (method, path, { body, params, external } = {}) => {
    const config = { ...requestConfig(scope, lifetime.current.signal) };
    if (params) config.params = params;
    if (external) config.baseURL = apiBase();
    const res = ['get', 'delete'].includes(method)
      ? await mktApi[method](path, config)
      : await mktApi[method](path, body, config);
    return res.data;
  }, [scope]);
  const run = useCallback(async (work, success) => {
    if (lock.current || !active()) return undefined;
    lock.current = true;
    setBusy(true); setError(''); setNotice('');
    try {
      const result = await work();
      if (active() && success) setNotice(success);
      return { ok: true, result };
    } catch (e) {
      if (active()) setError(errorMessage(e));
      return { ok: false, error: e };
    } finally {
      lock.current = false;
      if (active()) setBusy(false);
    }
  }, [active]);
  return { active, request, run, busy, error, notice, setError, setNotice };
}

// --- exact decimal helpers (strings in, strings out; never Number) ---------

const DECIMAL = /^\d+(\.\d+)?$/;
export const isDecimal = v => typeof v === 'string' && DECIMAL.test(v.trim());
const scaled = v => {
  const [whole, frac = ''] = v.trim().split('.');
  return { n: BigInt(whole + frac), s: frac.length };
};
const unscale = (n, s) => {
  const text = n.toString().padStart(s + 1, '0');
  return s === 0 ? text : `${text.slice(0, -s)}.${text.slice(-s)}`;
};
const align = (a, b) => {
  const x = scaled(a); const y = scaled(b);
  const s = Math.max(x.s, y.s);
  const ten = BigInt(10);
  return { x: x.n * ten ** BigInt(s - x.s), y: y.n * ten ** BigInt(s - y.s), s };
};
export const decCmp = (a, b) => { const { x, y } = align(a, b); return x === y ? 0 : x > y ? 1 : -1; };
export const decAdd = (a, b) => { const { x, y, s } = align(a, b); return unscale(x + y, s); };
export const decMul = (a, b) => { const x = scaled(a); const y = scaled(b); return unscale(x.n * y.n, x.s + y.s); };
export const isPositiveDecimal = v => isDecimal(v) && decCmp(v, '0') > 0;
export function displayDec(v, minScale = 2) {
  if (!isDecimal(String(v ?? ''))) return String(v ?? '');
  const [whole, frac = ''] = String(v).trim().split('.');
  let kept = frac.replace(/0+$/, '');
  if (kept.length < minScale) kept = kept.padEnd(minScale, '0');
  return kept ? `${whole}.${kept}` : whole;
}
export const money = (v, currency) => formatMoney(v, currency || 'GBP');
export const decimalProps = { type: 'text', inputMode: 'decimal', pattern: '[0-9]+([.][0-9]+)?', autoComplete: 'off' };

// --- ids -------------------------------------------------------------------

export function newUuid() {
  const c = window.crypto;
  if (c?.randomUUID) return c.randomUUID();
  if (!c?.getRandomValues) throw new Error('Secure random generator unavailable in this browser.');
  const b = new Uint8Array(16);
  c.getRandomValues(b);
  b[6] = (b[6] & 0x0f) | 0x40;
  b[8] = (b[8] & 0x3f) | 0x80;
  const h = Array.from(b, x => x.toString(16).padStart(2, '0')).join('');
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}

/**
 * Idempotency keys that survive retries. The same action fingerprint always maps to the
 * same key until `release` is called (on success); any change to the payload changes the
 * fingerprint and therefore mints a new key, so a key is never reused with a different body.
 */
export function useStableIds() {
  const ids = useRef(new Map());
  const idFor = useCallback(fingerprint => {
    const key = typeof fingerprint === 'string' ? fingerprint : JSON.stringify(fingerprint);
    if (!ids.current.has(key)) ids.current.set(key, newUuid());
    return ids.current.get(key);
  }, []);
  const release = useCallback(fingerprint => {
    ids.current.delete(typeof fingerprint === 'string' ? fingerprint : JSON.stringify(fingerprint));
  }, []);
  return { idFor, release };
}

// --- presentation ----------------------------------------------------------

export const shortId = id => (id ? String(id).slice(0, 8) : '');
export const statusLabel = s => String(s || '').replaceAll('_', ' ');
export const formatWhen = ts => {
  if (!ts) return '';
  const d = new Date(ts);
  return Number.isNaN(d.getTime()) ? String(ts) : d.toISOString().replace('T', ' ').slice(0, 16) + ' UTC';
};

export function Field({ label, options, children, ...props }) {
  return (
    <label className="mkt-field">
      <span>{label}</span>
      {options
        ? <select aria-label={label} {...props}>{options.map(o => <option key={o.value ?? o} value={o.value ?? o}>{o.label ?? o}</option>)}</select>
        : <input aria-label={label} {...props} />}
      {children}
    </label>
  );
}

export function Table({ headers, children, empty, emptyText = 'No records.', label }) {
  return (
    <div className="mkt-table-wrap" tabIndex={0} role="region" aria-label={`${label || headers[0]} records`}>
      <table>
        <thead><tr>{headers.map(h => <th key={h}>{h}</th>)}</tr></thead>
        <tbody>{children}</tbody>
      </table>
      {empty && <p className="mkt-empty">{emptyText}</p>}
    </div>
  );
}

export function Messages({ error, notice }) {
  return (
    <>
      {error && <div className="mkt-error" role="alert">{error}</div>}
      {notice && <div className="mkt-notice" role="status">{notice}</div>}
    </>
  );
}

/**
 * Searchable single-select. `options` are `{value,label}`; `loadOptions(text)` (optional)
 * fetches server-side matches for text of 2+ chars (debounced, stale responses ignored).
 */
export function SearchSelect({ label, value, onChange, options = [], loadOptions, disabled, emptyText = 'No matches' }) {
  const [text, setText] = useState('');
  const [open, setOpen] = useState(false);
  const [cursor, setCursor] = useState(0);
  const [remote, setRemote] = useState(null);
  const [failed, setFailed] = useState(false);
  const listId = useId();
  const seq = useRef(0);
  useEffect(() => {
    const query = text.trim();
    if (!loadOptions || query.length < 2) { seq.current += 1; setRemote(null); setFailed(false); return undefined; }
    const mine = ++seq.current;
    const timer = setTimeout(async () => {
      try {
        const found = await loadOptions(query);
        if (mine === seq.current) { setRemote(found); setFailed(false); }
      } catch { if (mine === seq.current) { setRemote(null); setFailed(true); } }
    }, 250);
    return () => clearTimeout(timer);
  }, [text, loadOptions]);
  if (value) {
    return (
      <div className="mkt-field" role="group" aria-label={label}>
        <span>{label}</span>
        <div className="mkt-chip">
          <span>{value.label}</span>
          <button type="button" aria-label={`Clear ${label}`} disabled={disabled} onClick={() => { onChange(null); setText(''); }}><X size={14} /></button>
        </div>
      </div>
    );
  }
  const needle = text.trim().toLowerCase();
  const pool = remote || options;
  const shown = pool.filter(o => !needle || o.label.toLowerCase().includes(needle)).slice(0, 50);
  const choose = option => { if (!option) return; onChange(option); setText(''); setOpen(false); setRemote(null); };
  const onKeyDown = e => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setOpen(true); setCursor(c => Math.min(c + 1, Math.max(shown.length - 1, 0))); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setCursor(c => Math.max(c - 1, 0)); }
    else if (e.key === 'Enter' && open && shown[cursor]) { e.preventDefault(); choose(shown[cursor]); }
    else if (e.key === 'Escape') setOpen(false);
  };
  return (
    <div className="mkt-field mkt-select">
      <label>
        <span>{label}</span>
        <input
          role="combobox" aria-label={label} aria-expanded={open} aria-controls={listId} aria-autocomplete="list"
          value={text} disabled={disabled} autoComplete="off" placeholder="Type to search"
          onChange={e => { setText(e.target.value); setOpen(true); setCursor(0); }}
          onFocus={() => setOpen(true)} onBlur={() => setTimeout(() => setOpen(false), 120)} onKeyDown={onKeyDown}
        />
      </label>
      {open && (
        <ul id={listId} role="listbox" aria-label={`${label} options`}>
          {shown.map((o, i) => (
            <li key={o.value} role="option" aria-selected={i === cursor} className={i === cursor ? 'mkt-active' : ''}
              onMouseDown={e => { e.preventDefault(); choose(o); }}>{o.label}</li>
          ))}
          {!shown.length && <li className="mkt-none" role="presentation">{failed ? 'Search unavailable' : emptyText}</li>}
        </ul>
      )}
    </div>
  );
}
